import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { brand } from '../branding';
import { isPromptSuppressedPath } from '../utils/promptSuppression';

// Persistent prompt shown when a new service worker has finished installing
// and is sitting in the "waiting" state. The user picks when to apply it —
// we don't auto-reload because they may be mid-form.
//
// vite-plugin-pwa's `useRegisterSW` exposes the SW lifecycle in a hook:
//   - needRefresh: a new version is waiting
//   - updateServiceWorker(true): activate it and reload
//   - offlineReady: first install succeeded; we don't surface this UI today
//     because it's a one-time event with little value to staff/parents.
//
// Update-check cadence: react-router's pushState-driven navigation does NOT
// cause the browser to re-fetch service-worker.js — there's no document load,
// just URL bar + component swap — so without an explicit check an installed
// PWA (no browser chrome, no reload button) sits on stale code for days.
// That is why registration.update() is called on every SPA route change.
// Cost is negligible:
// service-worker.js is a ~25KB S3-backed asset behind CloudFront, no Lambda
// invocation involved, and Workbox short-circuits when the byte content
// matches the running SW (most checks). Throttle to once per 30s to avoid
// hammering the network when a user clicks rapidly across nav items.

const UPDATE_CHECK_THROTTLE_MS = 30 * 1000;

// How long a dismissal defers the prompt. A dismissal must only SNOOZE it,
// never kill it for the page session: if nothing re-surfaces the prompt, the
// waiting SW never activates and the client keeps executing the old bundle out
// of the workbox precache indefinitely — every deploy (including a UI-layer
// security fix) stays invisible to installed clients until each user happens
// to hard-reload. "Later" means later, not never.
const DISMISS_SNOOZE_MS = 10 * 60 * 1000;

// A pending update also applies itself on the next full page load. `needRefresh`
// only reflects a SW waiting in THIS document; a reload re-registers, and the
// waiting worker is still there. Nudging it to skipWaiting on unload means a
// user who dismisses and later reopens the app gets the new build without ever
// having to accept a prompt.
const SwUpdatePrompt: React.FC = () => {
  const location = useLocation();
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const lastCheckRef = useRef<number>(0);
  const snoozeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
    onRegisterError(err) {
      // Hidden by default — surface during dev if SW registration fails.
      // eslint-disable-next-line no-console
      console.error('SW registration failed:', err);
    },
  });

  // Trigger an SW update check on every SPA navigation, throttled. Workbox
  // will only fire `needRefresh` if the network-fetched SW is actually
  // newer than the installed one, so the user only sees the banner when
  // there's a real update to apply.
  useEffect(() => {
    const reg = registrationRef.current;
    if (!reg) return;
    const now = performance.now();
    if (now - lastCheckRef.current < UPDATE_CHECK_THROTTLE_MS) return;
    lastCheckRef.current = now;
    // Fire-and-forget. .update() returns a promise but we don't gate the
    // UI on it — Workbox's onNeedRefresh handler flips `needRefresh` for
    // us if a new SW actually installs.
    reg.update().catch(() => {
      /* network blip, transient SW state — ignore, we'll try again next nav */
    });
  }, [location.pathname]);

  // Dismissal is a SNOOZE, not a cancel. Re-arm the prompt so a user who
  // clicks "Later" or "×" is asked again rather than being stranded on the
  // old build for the life of the tab.
  const snoozePrompt = () => {
    setNeedRefresh(false);
    if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
    snoozeTimerRef.current = setTimeout(() => {
      // Only re-surface if an update is genuinely still waiting.
      if (registrationRef.current?.waiting) setNeedRefresh(true);
    }, DISMISS_SNOOZE_MS);
  };

  useEffect(() => {
    return () => {
      if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
    };
  }, []);

  // Safety net: if a new SW is waiting when the page goes away, let it take
  // over so the NEXT visit starts on the new build. Without this, a user who
  // never accepts the prompt never gets the update at all. Safe to do on
  // unload — the current document is already being torn down, so the
  // stale-chunk hazard the Reload button guards against does not apply.
  useEffect(() => {
    const activateOnExit = () => {
      const waiting = registrationRef.current?.waiting;
      if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' });
    };
    window.addEventListener('pagehide', activateOnExit);
    return () => window.removeEventListener('pagehide', activateOnExit);
  }, []);

  // Sniff whether the InstallPrompt is currently mounted so we can stack
  // the Update prompt above it instead of colliding in the bottom-right
  // corner. Both components are absolutely positioned; without this the
  // install banner (which renders later) covers the update banner.
  const [installPromptHeight, setInstallPromptHeight] = useState(0);
  useEffect(() => {
    if (!needRefresh) return;
    const measure = () => {
      const el = document.querySelector('[data-testid="install-prompt"]');
      setInstallPromptHeight(el ? Math.ceil(el.getBoundingClientRect().height) + 12 : 0);
    };
    measure();
    // Re-measure when the DOM changes (install prompt mount/unmount).
    const obs = new MutationObserver(measure);
    obs.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [needRefresh]);

  if (!needRefresh) return null;
  // Never cover a safety-critical, time-pressured action bar. The live fire
  // drill and its wrap-up both render a `sticky bottom-0` footer holding the
  // primary action ("Complete drill" / "Save drill record"), and this fixed
  // bottom prompt lands squarely on top of it — a staff member finishing a
  // statutory drill could tap and have nothing happen. An update notice is
  // never more urgent than that, so defer it until they leave the flow.
  if (isPromptSuppressedPath(location.pathname)) return null;

  return (
    <div
      data-testid="sw-update-prompt"
      role="status"
      // Bottom offset is dynamic — the install prompt sits at bottom-4 too,
      // so when it's present we slide the update prompt above it.
      style={installPromptHeight ? { bottom: `${installPromptHeight + 16}px` } : undefined}
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40 rounded-xl bg-white border border-gray-200 shadow-xl p-4"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0"
        >
          <svg
            className="h-5 w-5 text-primary-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12a9 9 0 11-3-6.7L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900" data-testid="sw-update-headline">
            Update available
          </p>
          <p className="mt-0.5 text-xs text-gray-600">
            A new version of {brand.name} is ready. Reload to apply it.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              data-testid="sw-update-reload"
              onClick={async () => {
                // Two-step handoff:
                //   1. Tell the new SW to skipWaiting via
                //      updateServiceWorker(true) — but DO NOT reload yet.
                //   2. Wait for `controllerchange` to confirm the new SW
                //      has actually taken control. Only then reload.
                //
                // Reloading too early lands the user on a page still
                // controlled by the OLD SW, which serves cached
                // index.html referencing stale JS chunk URLs. Those
                // chunks have been deleted from S3 by the new deploy,
                // CloudFront serves index.html in their place, the
                // browser sees `text/html` where it expected JS, React
                // never mounts, and the user sees a blank screen until
                // they force-reload (Ctrl+Shift+R) to bypass the SW.
                //
                // Belt-and-braces: also set a 3s timeout in case
                // controllerchange never fires (e.g. first-ever SW
                // install in a tab — no previous controller to swap).
                const controllerChanged = new Promise<void>((resolve) => {
                  navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), {
                    once: true,
                  });
                  setTimeout(resolve, 3000);
                });
                await updateServiceWorker(true);
                await controllerChanged;
                window.location.reload();
              }}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
            >
              Reload
            </button>
            <button
              data-testid="sw-update-later"
              onClick={snoozePrompt}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
            >
              Later
            </button>
          </div>
        </div>
        <button
          data-testid="sw-update-dismiss"
          onClick={snoozePrompt}
          aria-label="Dismiss"
          className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

export default SwUpdatePrompt;
